/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Divider,
  IconButton,
  Alert,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NotesIcon from '@mui/icons-material/Notes';
import HealingIcon from '@mui/icons-material/Healing';
import Swal from 'sweetalert2';
import MDButton from 'components/MDButton';
import MDDatePicker from 'components/MDDatePicker';
import RichTextEditor from 'components/RichTextEditor';
import { api } from '../../api';

const TEAL = '#00798E';
const TEAL_LIGHT = 'rgba(0, 121, 142, 0.07)';
const TEAL_BORDER = 'rgba(0, 121, 142, 0.3)';

const selectSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TEAL },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL, borderWidth: '2px' },
};

const textSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: TEAL },
    '&.Mui-focused fieldset': { borderColor: TEAL, borderWidth: '2px' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: TEAL },
};

function SectionLabel({ icon: Icon, title }) {
  return (
    <Box display="flex" alignItems="center" gap={0.75} mb={1}>
      <Icon sx={{ color: TEAL, fontSize: 15 }} />
      <Typography
        variant="caption"
        sx={{ color: TEAL, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.68rem' }}
      >
        {title}
      </Typography>
    </Box>
  );
}

function FollowUpDialog({
  open,
  onClose,
  onSuccess,
  contactId,
  defaultDoctorId = null,
  prescriptionId = null,
  treatmentLine = null,
  currentUserId,
}) {
  const [form, setForm] = useState({
    irm_users_id: '',
    probable_date: '',
    reason: '',
    remarks: '',
    treatment_line: '',
  });
  const [doctors, setDoctors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [error, setError] = useState('');

  const minDate = useMemo(() => dayjs().add(1, 'day'), []);

  useEffect(() => {
    if (!open) return;

    setForm({
      irm_users_id: defaultDoctorId || '',
      probable_date: '',
      reason: '',
      remarks: '',
      treatment_line: treatmentLine || '',
    });
    setError('');

    const fetchDoctors = async () => {
      setLoadingDoctors(true);
      try {
        const resp = await api.contacts.getDoctorsList();
        setDoctors(Array.isArray(resp?.data) ? resp.data : []);
      } catch (e) {
        console.error('Failed to load doctors:', e);
        setDoctors([]);
      } finally {
        setLoadingDoctors(false);
      }
    };
    fetchDoctors();
  }, [open, defaultDoctorId, treatmentLine]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setError('');
    const today = new Date().toISOString().split('T')[0];
    if (!form.irm_users_id) { setError('Please select a doctor.'); return; }
    if (!form.probable_date) { setError('Please select a probable date.'); return; }
    if (form.probable_date <= today) { setError('Probable date must be a future date.'); return; }

    setSaving(true);
    try {
      const payload = {
        irm_contacts_id: contactId,
        irm_prescriptions_id: prescriptionId || null,
        irm_users_id: Number(form.irm_users_id),
        probable_date: form.probable_date,
        reason: form.reason ? form.reason.toUpperCase() : null,
        remarks: form.remarks || null,
        treatment_line: form.treatment_line || null,
        created_by: currentUserId,
      };

      // ApiClient unwraps response.data, so resp is already {statusCode, flag, data, message}
      const resp = await api.opd.createFollowup(payload);
      if (resp?.flag === 1 || resp?.statusCode === 201 || resp?.statusCode === 200) {
        await Swal.fire({
          icon: 'success',
          title: 'Follow-Up Created',
          text: resp?.message || 'Follow-up has been created successfully.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#00798E',
          timer: 2500,
          timerProgressBar: true,
        });
        onSuccess?.(resp.data);
        onClose();
      } else {
        setError(resp?.message || 'Failed to create follow-up.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to create follow-up.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: TEAL,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <EventNoteIcon sx={{ color: '#ffffff', fontSize: 22 }} />
          <Typography
            variant="h6"
            sx={{ color: '#ffffff !important', fontWeight: 600, fontSize: '1rem', lineHeight: 1.3 }}
          >
            Create Follow-Up
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#ffffff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ p: 3, pt: '24px !important' }}>
        <Grid container spacing={2.5}>

          {/* Consulting Doctor */}
          <Grid item xs={12}>
            <SectionLabel icon={PersonIcon} title="Consulting Doctor" />
            <FormControl fullWidth size="small">
              <InputLabel
                id="fu-doctor-label"
                shrink
                sx={{ bgcolor: 'white', px: 0.5, '&.Mui-focused': { color: TEAL } }}
              >
                {loadingDoctors ? 'Loading consultants…' : 'Consultant / Doctor *'}
              </InputLabel>
              <Select
                labelId="fu-doctor-label"
                label={loadingDoctors ? 'Loading consultants…' : 'Consultant / Doctor *'}
                value={form.irm_users_id}
                onChange={e => handleChange('irm_users_id', e.target.value)}
                notched
                displayEmpty
                disabled={loadingDoctors}
                sx={{ ...selectSx, height: 40 }}
              >
                <MenuItem value="" disabled>
                  <em>Select a consultant</em>
                </MenuItem>
                {doctors
                  .filter(d => d?.user_id && d?.name)
                  .map((d, i) => (
                    <MenuItem key={`doc-${d.user_id}-${i}`} value={d.user_id}>
                      {d.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Probable Date */}
          <Grid item xs={12}>
            <SectionLabel icon={CalendarMonthIcon} title="Probable Date" />
            <MDDatePicker
              label="Probable Date *"
              value={form.probable_date}
              onChange={dateStr => handleChange('probable_date', dateStr)}
              minDate={minDate}
              textFieldProps={{
                fullWidth: true,
                size: 'small',
                sx: textSx,
              }}
            />
          </Grid>

          {/* Reason */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Reason for Follow-Up"
              value={form.reason}
              onChange={e => handleChange('reason', e.target.value.toUpperCase())}
              inputProps={{ style: { textTransform: 'uppercase' } }}
              placeholder="E.G. POST-TREATMENT REVIEW, LAB RESULTS FOLLOW-UP..."
              sx={textSx}
            />
          </Grid>

          {/* Remarks */}
          <Grid item xs={12}>
            <SectionLabel icon={NotesIcon} title="Remarks" />
            <RichTextEditor
              value={form.remarks}
              onChange={val => handleChange('remarks', val)}
              height="120px"
              placeholder="ENTER ADDITIONAL REMARKS..."
            />
          </Grid>

          {/* Treatment Line */}
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: TEAL_LIGHT, borderColor: TEAL_BORDER, borderRadius: 2 }}
            >
              <SectionLabel icon={HealingIcon} title="Treatment Line (From Prescription)" />
              <TextField
                fullWidth
                size="small"
                label="Treatment / Diagnosis"
                value={form.treatment_line}
                onChange={e => handleChange('treatment_line', e.target.value.toUpperCase())}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                multiline
                rows={2}
                placeholder="AUTO-POPULATED FROM PRESCRIPTION DIAGNOSIS..."
                sx={textSx}
              />
            </Paper>
          </Grid>

          {/* Error */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>
            </Grid>
          )}

        </Grid>
      </DialogContent>

      {/* Footer */}
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="logo"
          onClick={handleSubmit}
          disabled={saving || loadingDoctors}
        >
          {saving ? 'Saving…' : 'Create Follow-Up'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default FollowUpDialog;
